import { motion, useAnimationControls, useReducedMotionConfig } from "motion/react";
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../../../cn.ts";
import { pixels } from "../../../theme/pixels.ts";
import { detectPlatform, glyphFor, type Platform } from "./keys.ts";

/**
 * § Primitives, Kbd, taken from Spectrum UI's Kbd Key and Kbd Combo (`@spectrumui/kbd-key`,
 * Apache-2.0) and themed through the variables of `packages/tokens` (2026-09-15, by the owner's
 * decision): each key is a cap with a depth edge that presses down while the real key is held
 * and springs back on release, and a combo pulses when every key in it is held at once. Its
 * springs are Spectrum UI's own and allowed by that decision; reduced motion, from the system
 * or from `data-motion="reduced"` through ThemeProvider's `MotionConfig`, moves nothing.
 * `Mod` is the command key on Apple platforms and Control elsewhere (§ Keyboard).
 *
 * What differs from the source, and why: the keys are written as Baton's shortcut map writes
 * them ("Mod+K", "g i", "Shift+Enter"), a space separating the steps of a sequence, with
 * Baton's platform glyphs; the cap is the small 24 px size with its label at the 11 px floor of
 * § Type where the source's small label is 10 px; the edge, cap and legend take the theme's
 * colours and the edge is the `--kbd-edge` token; a cap is never a button, because a shortcut
 * shown in a menu or a palette is a label of that control; the combo carries no trigger
 * callback, because the shortcut map owns what a key does; and the window `blur` listener is
 * removed under the name it was added with, where the source removes `blur-sm` and leaves it.
 */

/** Baton's key names as `KeyboardEvent.key` values, which the caps listen for. */
const EVENT_KEYS: Readonly<Record<Platform, Readonly<Record<string, string>>>> = {
  mac: { Mod: "Meta", Cmd: "Meta", Meta: "Meta", Ctrl: "Control", Alt: "Alt", Option: "Alt", Shift: "Shift", Enter: "Enter", Backspace: "Backspace", Delete: "Delete", Esc: "Escape", Tab: "Tab", Up: "ArrowUp", Down: "ArrowDown", Left: "ArrowLeft", Right: "ArrowRight" },
  other: { Mod: "Control", Cmd: "Control", Meta: "Meta", Ctrl: "Control", Alt: "Alt", Option: "Alt", Shift: "Shift", Enter: "Enter", Backspace: "Backspace", Delete: "Delete", Esc: "Escape", Tab: "Tab", Up: "ArrowUp", Down: "ArrowDown", Left: "ArrowLeft", Right: "ArrowRight" },
};

/** Lowercased `KeyboardEvent.key` values of the modifier keys. */
const MODIFIERS = new Set(["meta", "control", "shift", "alt"]);

/** How far the cap travels down while pressed, the depth of its edge. */
const PRESS_DEPTH = pixels("kbd-edge");
/** Press-down is near instant: real keys do not ease in. */
const PRESS_TRANSITION = { duration: 0.08, ease: "easeOut" } as const;
/** Release springs back with a small overshoot before settling. */
const RELEASE_SPRING = { type: "spring", stiffness: 500, damping: 18 } as const;
/** Whole-combo pulse when every key is down at once. */
const PULSE_SCALE = [1, 1.06, 1];
const PULSE_TRANSITION = { duration: 0.25, ease: "easeOut" } as const;

function eventKeyFor(key: string, platform: Platform): string {
  return (EVENT_KEYS[platform][key] ?? key).toLowerCase();
}

export interface KbdProps {
  /** Keys as written in the shortcut map: "Mod+K", "g i", "Shift+Enter". */
  readonly keys: string;
  readonly platform?: Platform;
}

export function Kbd({ keys, platform }: KbdProps) {
  const resolved = platform ?? detectPlatform(navigator.userAgent);
  return (
    <kbd data-slot="kbd" className="inline-flex items-center gap-2 font-sans">
      {/* A `kbd` takes no accessible name of its own (axe `aria-prohibited-attr`), so the keys are said as text and the caps' glyphs are hidden from a screen reader. */}
      <span className="sr-only">{keys}</span>
      {keys.split(" ").map((chord, index) => (
        <KbdCombo key={`${chord}-${String(index)}`} chord={chord} platform={resolved} />
      ))}
    </kbd>
  );
}

function KbdCombo({ chord, platform }: { readonly chord: string; readonly platform: Platform }) {
  const shouldReduceMotion = useReducedMotionConfig() === true;
  const controls = useAnimationControls();
  const names = chord.split("+").filter((name) => name.length > 0);
  const targetsKey = names.map((name) => eventKeyFor(name, platform)).join("+");

  useEffect(() => {
    const targets = targetsKey.split("+");
    const down = new Set<string>();
    // The physical key holding each target down, so a shifted keyup ("?" released as "/") still clears it.
    const codeToKey = new Map<string, string>();
    let fired = false;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      const key = event.key.toLowerCase();
      if (!targets.includes(key)) return;
      down.add(key);
      codeToKey.set(event.code, key);
      if (fired || !targets.every((target) => down.has(target))) return;
      fired = true;
      if (!shouldReduceMotion) void controls.start({ scale: PULSE_SCALE, transition: PULSE_TRANSITION });
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === "meta") {
        // macOS swallows non-modifier keyups while the command key is held; treat them as released with it.
        for (const held of down) if (!MODIFIERS.has(held)) down.delete(held);
        codeToKey.clear();
      }
      const mapped = codeToKey.get(event.code);
      if (mapped !== undefined) {
        down.delete(mapped);
        codeToKey.delete(event.code);
      }
      down.delete(key);
      if (!targets.every((target) => down.has(target))) fired = false;
    };
    const handleBlur = () => {
      down.clear();
      codeToKey.clear();
      fired = false;
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [targetsKey, shouldReduceMotion, controls]);

  return (
    <motion.span animate={controls} aria-hidden="true" className="inline-flex items-center gap-1">
      {names.map((name, index) => (
        <Fragment key={`${name}-${String(index)}`}>
          {index > 0 && (
            <span aria-hidden="true" className="text-micro text-muted-foreground select-none">
              +
            </span>
          )}
          <KbdKey eventKey={eventKeyFor(name, platform)}>{glyphFor(name, platform)}</KbdKey>
        </Fragment>
      ))}
    </motion.span>
  );
}

function KbdKey({ eventKey, children }: { readonly eventKey: string; readonly children: ReactNode }) {
  const shouldReduceMotion = useReducedMotionConfig() === true;
  const [pressed, setPressed] = useState(false);
  // Mirrors `pressed` so press and release stay idempotent without re-creating the window listeners.
  const pressedRef = useRef(false);
  // The physical key that pressed the cap, which releases it even when dropping Shift changed `event.key`.
  const pressedCodeRef = useRef<string | null>(null);

  const press = useCallback(() => {
    if (pressedRef.current) return;
    pressedRef.current = true;
    setPressed(true);
  }, []);

  const release = useCallback(() => {
    if (!pressedRef.current) return;
    pressedRef.current = false;
    pressedCodeRef.current = null;
    setPressed(false);
  }, []);

  useEffect(() => {
    const targetIsModifier = MODIFIERS.has(eventKey);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.key.toLowerCase() !== eventKey) return;
      pressedCodeRef.current = event.code;
      press();
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      const releasedMatchingCode = pressedCodeRef.current !== null && event.code === pressedCodeRef.current;
      // macOS suppresses keyup for non-modifier keys while the command key is held, so releasing it also releases this cap.
      const metaSwallowedKeyUp = event.key.toLowerCase() === "meta" && !targetIsModifier;
      if (event.key.toLowerCase() === eventKey || releasedMatchingCode || metaSwallowedKeyUp) release();
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", release);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", release);
    };
  }, [eventKey, press, release]);

  return (
    <motion.kbd
      aria-hidden="true"
      initial={false}
      animate={{ y: pressed ? PRESS_DEPTH : 0 }}
      transition={shouldReduceMotion ? { duration: 0 } : pressed ? PRESS_TRANSITION : RELEASE_SPRING}
      className={cn(
        "inline-flex h-(--kbd-height) min-w-(--kbd-height) items-center justify-center rounded-md border border-border-subtle px-(--kbd-padding-x) font-mono text-micro text-muted-foreground select-none",
        pressed ? "bg-secondary shadow-none" : "bg-muted shadow-kbd",
        !shouldReduceMotion && "transition-[background-color,box-shadow] ease-out",
      )}
    >
      {children}
    </motion.kbd>
  );
}
